<template>
  <div>
    <!-- Active indicator badge -->
    <b-card
      v-if="active && active.indicator"
      no-body
      class="mb-2"
    >
      <b-card-body class="d-flex align-items-center">
        <feather-icon
          icon="ZapIcon"
          size="24"
          class="text-warning mr-1"
        />
        <div class="flex-grow-1">
          <div class="font-weight-bold">
            Aktif İndikatör: {{ active.indicator.name }}
          </div>
          <div class="font-small-3 text-muted">
            {{ active.indicator.engineKey }} · {{ active.indicator.family }} v{{ active.indicator.version }}
            <span v-if="active.label"> · {{ active.label }}</span>
            <span
              v-if="active.paramsOverride"
              class="ml-1 text-info"
            >[custom params]</span>
          </div>
        </div>
        <b-button
          variant="outline-danger"
          size="sm"
          @click="onDeactivate"
        >
          <feather-icon icon="PowerIcon" /> Pasif Yap
        </b-button>
      </b-card-body>
    </b-card>
    <b-card
      v-else
      no-body
      class="mb-2"
    >
      <b-card-body class="text-center text-muted">
        <feather-icon
          icon="AlertCircleIcon"
          size="20"
        />
        Henüz aktif bir indikatör seçilmemiş. Aşağıdan birini seç + aktifleştir.
      </b-card-body>
    </b-card>

    <!-- Indicator list -->
    <b-card no-body>
      <b-card-body class="d-flex align-items-center">
        <h4 class="mb-0">
          İndikatör Listesi ({{ dataList.length }})
        </h4>
        <b-button
          variant="outline-primary"
          size="sm"
          class="ml-auto"
          :disabled="syncing"
          @click="onSync"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :class="{ 'spin': syncing }"
          /> Registry Sync
        </b-button>
      </b-card-body>
      <b-table
        responsive="sm"
        :fields="fields"
        :items="dataList"
        :busy="loading"
        striped
        show-empty
        empty-text="Henüz engine kayıtlı değil. 'Registry Sync' ile sunucudaki engine'leri DB'ye yaz."
      >
        <template #cell(name)="data">
          <div class="font-weight-bold">
            {{ data.item.name }}
          </div>
          <div class="font-small-3 text-muted">
            {{ data.item.engineKey }}
          </div>
        </template>
        <template #cell(family)="data">
          <b-badge :variant="familyVariant(data.item.family)">
            {{ data.item.family }}
          </b-badge>
        </template>
        <template #cell(version)="data">
          <span class="font-small-3">v{{ data.item.version }}</span>
        </template>
        <template #cell(description)="data">
          <div
            class="font-small-3 text-muted"
            style="max-width:400px;"
          >
            {{ data.item.description || '-' }}
          </div>
        </template>
        <template #cell(actions)="data">
          <div class="text-right">
            <b-button
              variant="outline-warning"
              size="sm"
              class="mr-1"
              @click="onBacktest(data.item)"
            >
              <feather-icon icon="PlayCircleIcon" /> Test
            </b-button>
            <b-button
              v-if="!isActive(data.item.id)"
              variant="primary"
              size="sm"
              @click="onActivate(data.item)"
            >
              <feather-icon icon="ZapIcon" /> Aktifleştir
            </b-button>
            <b-button
              v-else
              variant="success"
              size="sm"
              disabled
            >
              <feather-icon icon="CheckCircleIcon" /> Aktif
            </b-button>
          </div>
        </template>
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small /> Yükleniyor...
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Backtest modal -->
    <b-modal
      v-model="backtestModal.show"
      :title="`Backtest — ${backtestModal.engineName}`"
      size="xl"
      hide-footer
      no-close-on-backdrop
    >
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Sembol</label>
          <b-form-input
            v-model="backtestForm.symbol"
            placeholder="BTCUSDT"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Timeframe</label>
          <b-form-select
            v-model="backtestForm.timeframe"
            :options="timeframeOptions"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Market</label>
          <b-form-select
            v-model="backtestForm.market"
            :options="marketOptions"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">TP %</label>
          <b-form-input
            v-model.number="backtestForm.tpPerc"
            type="number"
            step="0.1"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">SL %</label>
          <b-form-input
            v-model.number="backtestForm.slPerc"
            type="number"
            step="0.1"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Kaldıraç</label>
          <b-form-input
            v-model.number="backtestForm.leverage"
            type="number"
            step="1"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Komisyon/Yön %</label>
          <b-form-input
            v-model.number="backtestForm.commissionPerSide"
            type="number"
            step="0.01"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Mum Sayısı (max 1500)</label>
          <b-form-input
            v-model.number="backtestForm.candleLimit"
            type="number"
            step="100"
            min="100"
            max="1500"
          />
        </b-col>
      </b-row>

      <div class="text-right mb-2">
        <b-button
          variant="warning"
          :disabled="backtestLoading"
          @click="runBacktest"
        >
          <feather-icon icon="PlayIcon" /> Backtest Çalıştır
        </b-button>
      </div>

      <div
        v-if="backtestLoading"
        class="text-center py-3"
      >
        <b-spinner /> Backtest çalışıyor... (Binance'tan {{ backtestForm.candleLimit }} mum çekiliyor + engine analizi)
      </div>

      <!-- Backtest result -->
      <div v-if="backtestResult && !backtestLoading">
        <hr>
        <h5>Kalite Özeti</h5>
        <b-row>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Toplam Trade:</strong> {{ backtestResult.quality.totalTrades }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Win Rate:</strong> {{ (backtestResult.quality.winRate || 0).toFixed(1) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><strong>Net PnL:</strong>
            <span :class="(backtestResult.quality.netCumulativePnl || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ (backtestResult.quality.netCumulativePnl || 0).toFixed(2) }}%
            </span>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Gross PnL:</strong> {{ (backtestResult.quality.grossCumulativePnl || 0).toFixed(2) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><strong>Net EV/Trade:</strong>
            <span :class="(backtestResult.quality.netExpectancyPerTrade || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ (backtestResult.quality.netExpectancyPerTrade || 0).toFixed(3) }}%
            </span>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Break-even WR:</strong> {{ (backtestResult.quality.breakEvenWinRate || 0).toFixed(1) }}%
          </b-col>
          <b-col
            cols="6"
            md="3"
          ><strong>WR Gap:</strong>
            <span :class="(backtestResult.quality.winRateGap || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ (backtestResult.quality.winRateGap || 0).toFixed(1) }} puan
            </span>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Komisyon RT:</strong> {{ (backtestResult.quality.commissionRoundTrip || 0).toFixed(2) }}%
          </b-col>
        </b-row>

        <hr>
        <h5>Diagnostics</h5>
        <b-row>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Bars:</strong> {{ backtestResult.diagnostics.bars }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Eligible:</strong> {{ backtestResult.diagnostics.evaluatedBars }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Raw Candidates:</strong> {{ backtestResult.diagnostics.rawCandidates }}
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <strong>Entries / Exits:</strong> {{ backtestResult.diagnostics.entries }} / {{ backtestResult.diagnostics.exits }}
          </b-col>
        </b-row>

        <hr>
        <div class="d-flex align-items-center mb-1">
          <h5 class="mb-0">
            Trade Geçmişi
          </h5>
          <small class="text-muted ml-2">
            (En son {{ Math.min(tradePairs.length, 30) }} trade gösteriliyor — toplam {{ tradePairs.length }})
          </small>
          <b-form-checkbox
            v-model="showOpenTrades"
            switch
            class="ml-auto"
          >
            Açık pozisyonları da göster
          </b-form-checkbox>
        </div>
        <b-table
          :items="visibleTradePairs"
          :fields="tradeFields"
          small
          striped
          responsive
          show-empty
          empty-text="Henüz tamamlanmış trade yok."
        >
          <template #cell(idx)="data">
            #{{ data.item.idx }}
          </template>
          <template #cell(side)="data">
            <b-badge :variant="data.item.side === 'LONG' ? 'success' : 'danger'">
              {{ data.item.side }}
            </b-badge>
          </template>
          <template #cell(entryTime)="data">
            <div class="font-small-3">
              {{ formatDateTime(data.item.entryTime) }}
            </div>
            <div class="font-small-2 text-muted">
              bar {{ data.item.entryIndex }}
            </div>
          </template>
          <template #cell(entryPrice)="data">
            <span class="font-small-3">{{ formatPrice(data.item.entryPrice) }}</span>
          </template>
          <template #cell(exitTime)="data">
            <div
              v-if="data.item.exitTime"
              class="font-small-3"
            >
              {{ formatDateTime(data.item.exitTime) }}
            </div>
            <div
              v-if="data.item.exitIndex !== null"
              class="font-small-2 text-muted"
            >
              bar {{ data.item.exitIndex }}
            </div>
            <div
              v-if="!data.item.exitTime"
              class="font-small-3 text-warning"
            >
              <feather-icon icon="LoaderIcon" /> Açık
            </div>
          </template>
          <template #cell(exitPrice)="data">
            <span
              v-if="data.item.exitPrice !== null"
              class="font-small-3"
            >
              {{ formatPrice(data.item.exitPrice) }}
            </span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
          <template #cell(duration)="data">
            <span
              v-if="data.item.durationBars !== null"
              class="font-small-3"
            >
              {{ formatDuration(data.item.durationBars) }}
            </span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
          <template #cell(profit)="data">
            <span
              v-if="data.item.profit !== null"
              :class="data.item.profit >= 0 ? 'text-success' : 'text-danger'"
              class="font-weight-bold"
            >
              {{ data.item.profit >= 0 ? '+' : '' }}{{ data.item.profit.toFixed(3) }}%
            </span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
          <template #cell(reason)="data">
            <div
              class="font-small-3 text-muted"
              style="max-width:300px;"
            >
              {{ data.item.exitReason || data.item.entryReason || '-' }}
            </div>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BTable, BButton, BBadge, BSpinner,
  BModal, BFormInput, BFormSelect, BFormCheckbox, BRow, BCol,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'IndicatorsIndex',
  components: {
    BCard,
    BCardBody,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BModal,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BRow,
    BCol,
  },
  data() {
    return {
      loading: false,
      syncing: false,
      showOpenTrades: true,
      fields: [
        { key: 'id', label: '#', thClass: 'width-50' },
        { key: 'name', label: 'İndikatör' },
        { key: 'family', label: 'Aile', thClass: 'width-100' },
        { key: 'version', label: 'Sürüm', thClass: 'width-80' },
        { key: 'description', label: 'Açıklama' },
        { key: 'actions', label: '', thClass: 'width-200 text-nowrap' },
      ],
      tradeFields: [
        { key: 'idx', label: '#', thClass: 'width-50' },
        { key: 'side', label: 'Yön', thClass: 'width-80' },
        { key: 'entryTime', label: 'Giriş', thClass: 'width-150' },
        { key: 'entryPrice', label: 'Giriş Fiyat', thClass: 'width-120' },
        { key: 'exitTime', label: 'Çıkış', thClass: 'width-150' },
        { key: 'exitPrice', label: 'Çıkış Fiyat', thClass: 'width-120' },
        { key: 'duration', label: 'Süre', thClass: 'width-100' },
        { key: 'profit', label: 'K/Z', thClass: 'width-100' },
        { key: 'reason', label: 'Sebep' },
      ],
      timeframeOptions: ['1m', '5m', '15m', '30m', '1h', '4h', '1d'].map(v => ({ text: v, value: v })),
      marketOptions: [
        { text: 'Futures', value: 'FUTURES' },
        { text: 'Spot', value: 'SPOT' },
      ],
      backtestModal: { show: false, engineKey: '', engineName: '' },
      backtestForm: {
        symbol: 'BTCUSDT',
        timeframe: '5m',
        market: 'FUTURES',
        candleLimit: 500,
        tpPerc: 0.5,
        slPerc: 0.3,
        leverage: 10,
        commissionPerSide: 0.05,
      },
    }
  },
  computed: {
    dataList() { return this.$store.getters['indicators/dataList'] },
    active() { return this.$store.getters['indicators/active'] },
    backtestResult() { return this.$store.getters['indicators/backtestResult'] },
    backtestLoading() { return this.$store.getters['indicators/backtestLoading'] },
    saveStatus() { return this.$store.getters['indicators/dataSaveStatus'] },

    // ENTRY/EXIT sinyallerini pozisyon çiftlerine dönüştür (kronolojik)
    tradePairs() {
      if (!this.backtestResult || !this.backtestResult.signals) return []
      const pairs = []
      let openEntry = null
      for (const sig of this.backtestResult.signals) {
        if (sig.action === 'ENTRY') {
          // Eğer önceki ENTRY hâlâ açıksa (engine sebepsiz yere flush etmemişse)
          if (openEntry) {
            pairs.push(this.makePair(openEntry, null))
          }
          openEntry = sig
        } else if (sig.action === 'EXIT' && openEntry) {
          pairs.push(this.makePair(openEntry, sig))
          openEntry = null
        }
      }
      // Açıkta kalan son ENTRY (henüz EXIT olmamış)
      if (openEntry) pairs.push(this.makePair(openEntry, null))
      // Sıra: en yeni en üstte
      return pairs.map((p, i) => ({ ...p, idx: pairs.length - i })).reverse()
    },

    visibleTradePairs() {
      let list = this.tradePairs
      if (!this.showOpenTrades) {
        list = list.filter(p => p.exitTime !== null)
      }
      return list.slice(0, 30)
    },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Başarılı', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  created() {
    this.fetchList()
    this.$store.dispatch('indicators/getActive')
  },
  methods: {
    async fetchList() {
      this.loading = true
      await this.$store.dispatch('indicators/getDataList', {})
      this.loading = false
    },
    async onSync() {
      this.syncing = true
      await this.$store.dispatch('indicators/syncRegistry')
      this.syncing = false
    },
    isActive(indicatorId) {
      return this.active && this.active.id_indicators === indicatorId
    },
    familyVariant(family) {
      const map = {
        sr: 'info', pullback: 'success', scalper: 'warning', deltaflow: 'primary',
      }
      return map[family] || 'secondary'
    },
    async onActivate(item) {
      this.$swal({
        title: `'${item.name}' aktif edilsin mi?`,
        text: 'Aktif olan indikatör (varsa) pasif yapılır.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Aktifleştir',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          await this.$store.dispatch('indicators/activate', { indicatorId: item.id })
        }
      })
    },
    async onDeactivate() {
      this.$swal({
        title: 'Pasif yapılsın mı?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Pasif Yap',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          await this.$store.dispatch('indicators/deactivate')
        }
      })
    },
    onBacktest(item) {
      this.backtestModal.show = true
      this.backtestModal.engineKey = item.engineKey
      this.backtestModal.engineName = item.name
      this.$store.commit('indicators/SET_BACKTEST_RESULT', null)
    },
    async runBacktest() {
      const body = {
        engineKey: this.backtestModal.engineKey,
        symbol: this.backtestForm.symbol,
        timeframe: this.backtestForm.timeframe,
        market: this.backtestForm.market,
        candleLimit: this.backtestForm.candleLimit,
        runtimeConfig: {
          tpPerc: this.backtestForm.tpPerc,
          slPerc: this.backtestForm.slPerc,
          leverage: this.backtestForm.leverage,
          commissionPerSide: this.backtestForm.commissionPerSide,
        },
      }
      await this.$store.dispatch('indicators/backtest', body)
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },

    // ─── Trade pair builder ──────────────────────────────────────────────
    makePair(entry, exit) {
      return {
        side: entry.type,
        entryIndex: entry.index,
        entryTime: entry.timestamp,
        entryPrice: entry.price,
        entryReason: entry.reason,
        exitIndex: exit ? exit.index : null,
        exitTime: exit ? exit.timestamp : null,
        exitPrice: exit ? exit.price : null,
        exitReason: exit ? exit.reason : null,
        durationBars: exit ? exit.index - entry.index : null,
        profit: exit ? (exit.profit ?? null) : null,
      }
    },

    // ─── Formatters ──────────────────────────────────────────────────────
    formatDateTime(value) {
      if (!value) return '—'
      try {
        const d = new Date(value)
        if (isNaN(d.getTime())) return String(value)
        return d.toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (e) {
        return String(value)
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined) return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      // Otomatik decimal sayısı: 1000+ → 2, 1-1000 → 4, <1 → 6
      let decimals = 4
      if (n >= 1000) decimals = 2
      else if (n < 1) decimals = 6
      return n.toLocaleString('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    },
    formatDuration(bars) {
      if (bars === null || bars === undefined) return '—'
      // TF'ye göre dakika hesabı
      const tfMins = {
        '1m': 1,
        '3m': 3,
        '5m': 5,
        '15m': 15,
        '30m': 30,
        '1h': 60,
        '2h': 120,
        '4h': 240,
        '1d': 1440,
      }
      const min = bars * (tfMins[this.backtestForm.timeframe] || 0)
      if (!min) return `${bars} bar`
      if (min < 60) return `${bars}b · ${min}dk`
      if (min < 1440) return `${bars}b · ${(min / 60).toFixed(1)}sa`
      return `${bars}b · ${(min / 1440).toFixed(1)}gün`
    },
  },
}
</script>

<style scoped>
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% { transform: rotate(360deg); }
}
</style>
